import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { getQuickLogActions } from './navigation'

/**
 * The mobile action sheet behind the centre button — VH-133
 *
 * Every entry navigates to a screen that already exists. It logs nothing
 * itself, holds no form state and calls no API: it is a shortcut surface, and
 * keeping it that way is what let the most visible piece of the redesign ship
 * without touching any logging logic.
 *
 * Headless UI's Dialog is used rather than a hand-rolled overlay for the parts
 * that are easy to get wrong and invisible when wrong: focus trapping, restore
 * on close, Escape handling, and marking the rest of the page inert for screen
 * readers.
 */
export default function QuickLogSheet({ open, onClose }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const actions = getQuickLogActions(t)

  const go = (href) => {
    onClose()
    navigate(href)
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50 md:hidden">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </Transition.Child>

        {/* Anchored to the bottom, above the nav bar it belongs to — a
            centred modal would put the choices far from the thumb that
            opened them. */}
        <div className="fixed inset-x-0 bottom-0 z-50 p-3 pb-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <Dialog.Panel className="mx-auto w-full max-w-sm overflow-hidden rounded-2xl bg-surface-container border border-outline-variant shadow-xl">
              <Dialog.Title className="px-4 pt-4 pb-2 label-caps text-gray-500">
                {t('quickLog.title')}
              </Dialog.Title>

              <div className="pb-2">
                {actions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    onClick={() => go(action.href)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-gray-800 hover:bg-gray-100 transition-colors"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-primary-600">
                      <action.icon className="h-5 w-5" />
                    </span>
                    <span className="text-sm font-medium">{action.name}</span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={onClose}
                className="w-full border-t border-outline-variant px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
